import { useCallback, useEffect, useRef } from 'react';

const MAX_TILT = 8; // degrees
const HOVER_TRANSITION = 'transform 0.1s ease-out';
const LEAVE_TRANSITION = 'transform 1s cubic-bezier(0.16, 1, 0.3, 1)';

export interface UsePanelTiltOptions {
  enabled?: boolean;
}

export function usePanelTilt({ enabled = true }: UsePanelTiltOptions = {}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const isHoveringRef = useRef(false);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const el = panelRef.current;
    if (!el || !isHoveringRef.current) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `rotateX(${y * -MAX_TILT}deg) rotateY(${x * MAX_TILT}deg) scale(1) translateZ(0)`;
  }, []);

  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
    const el = panelRef.current;
    if (!el) return;
    el.style.transition = HOVER_TRANSITION;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
    const el = panelRef.current;
    if (!el) return;
    el.style.transition = LEAVE_TRANSITION;
    el.style.transform = 'rotateX(0deg) rotateY(0deg) scale(1) translateZ(0)';
  }, []);

  useEffect(() => {
    if (enabled) return;
    isHoveringRef.current = false;
    const el = panelRef.current;
    if (!el) return;
    el.style.transform = '';
    el.style.transition = '';
  }, [enabled]);

  return { panelRef, handleMouseMove, handleMouseEnter, handleMouseLeave };
}
